import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IMessageContainers } from "../../../MessageComponents/Enums/MessageEnums";
import { getMessageContainerDisplayName } from "../../../MessageComponents/FunctionMethods/MessageFunctions";
import { RootState } from "../Store";

export interface ISelectedCandidate {
  candidateId: string;
  firstName: string;
  lastName: string;
  hasEmail: boolean;
  hasPhone: boolean;
  unsubscribed: boolean;
  emailAddress?: string;
  phoneNumber?: string;
}

export const defaultSelectedCandidate: ISelectedCandidate = {
  candidateId: "",
  firstName: "",
  lastName: "",
  hasEmail: false,
  hasPhone: false,
  unsubscribed: false,
};

export interface IMessageDialogState {
  disableOpen: boolean;
  isOpen: boolean;
  isCandidateEmailOpen: boolean;
  isCandidateTextOpen: boolean;
  isGroupTabsOpen: boolean;
  containerType: IMessageContainers;
  candidateIds: string[];
  candidateEntities: Record<string, ISelectedCandidate>;
  // Optional properties for group messaging
  groupId?: string;
  campaignId?: string;
  dialogTitle?: string;
  groupMemberCount?: number;
}

const defaultMessageDialogState: IMessageDialogState = {
  disableOpen: false,
  isOpen: false,
  isCandidateEmailOpen: false,
  isCandidateTextOpen: false,
  isGroupTabsOpen: false,
  containerType: IMessageContainers.None,
  candidateIds: [],
  candidateEntities: {},
};

export const MessageDialogSlice = createSlice({
  name: "messageDialog",
  initialState: defaultMessageDialogState,
  reducers: {
    showDialog: (state) => {
      state.isOpen = true;
    },
    hideDialog: (state) => {
      state.isOpen = false;
    },
    showCandidateEmailDialog: (state) => {
      state.isCandidateEmailOpen = true;
    },
    hideCandidateEmailDialog: (state) => {
      state.isCandidateEmailOpen = false;
    },
    showCandidateTextDialog: (state) => {
      state.isCandidateTextOpen = true;
    },
    hideCandidateTextDialog: (state) => {
      state.isCandidateTextOpen = false;
    },
    showGroupTabsDialog: (state) => {
      state.isGroupTabsOpen = true;
    },
    hideGroupTabsDialog: (state) => {
      state.isGroupTabsOpen = false;
    },
    setContainerType: (state, action: PayloadAction<IMessageContainers>) => {
      state.dialogTitle = state.dialogTitle ?? getMessageContainerDisplayName(action.payload);
      state.containerType = action.payload;
    },
    setDisableOpen: (state, action: PayloadAction<boolean>) => {
      state.disableOpen = action.payload;
    },
    addCandidates: (state, action: PayloadAction<ISelectedCandidate[]>) => {
      for (const c of action.payload) {
        if (!c.candidateId) {
          continue;
        }
        if (!state.candidateEntities[c.candidateId]) {
          state.candidateIds.push(c.candidateId);
        }
        state.candidateEntities[c.candidateId] = c;
      }
    },
    removeCandidatesById: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.candidateEntities[id]) {
        delete state.candidateEntities[id];
        state.candidateIds = state.candidateIds.filter((x) => x !== id);
      }
    },
    filterCandidates: (state, action: PayloadAction<(candidate: ISelectedCandidate) => boolean>) => {
      const keepIds: string[] = [];
      const newEntities: Record<string, ISelectedCandidate> = {};
      for (const id of state.candidateIds) {
        const candidate = state.candidateEntities[id];
        if (candidate && action.payload(candidate)) {
          keepIds.push(id);
          newEntities[id] = candidate;
        }
      }
      state.candidateIds = keepIds;
      state.candidateEntities = newEntities;
    },
    setGroupId: (state, action: PayloadAction<string>) => {
      state.groupId = action.payload;
    },
    setCampaignId: (state, action: PayloadAction<string>) => {
      state.campaignId = action.payload;
    },
    setDialogInitialState: (state, action: PayloadAction<Partial<IMessageDialogState>>) => {
      const tempState = {
        ...state,
        ...action.payload,
      } as IMessageDialogState;
      state.isOpen = tempState.isOpen;
      state.containerType = tempState.containerType;
      state.candidateIds = tempState.candidateIds ?? [];
      state.candidateEntities = tempState.candidateEntities ?? {};
      state.groupId = tempState.groupId;
      state.campaignId = tempState.campaignId;
      state.dialogTitle = tempState.dialogTitle ?? getMessageContainerDisplayName(tempState.containerType);
      state.groupMemberCount = tempState.groupMemberCount;
    },
    resetDialogState: (state) => {
      state.isOpen = false;
      setTimeout(() => {
        state = defaultMessageDialogState;
      }, 300);
    },
    setDialogTitle: (state, action: PayloadAction<string>) => {
      state.dialogTitle = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  showDialog,
  hideDialog,
  setContainerType,
  setDisableOpen,
  addCandidates,
  removeCandidatesById,
  filterCandidates,
  setGroupId,
  setCampaignId,
  setDialogInitialState,
  resetDialogState,
  setDialogTitle,
  showCandidateEmailDialog,
  hideCandidateEmailDialog,
  showCandidateTextDialog,
  hideCandidateTextDialog,
  showGroupTabsDialog,
  hideGroupTabsDialog,
} = MessageDialogSlice.actions;

export const selectMessageDialogState = (state: RootState) => state.messageDialog;
export const selectSelectedCandidates = createSelector(selectMessageDialogState, (state) =>
  Object.values(state.candidateEntities),
);
export const selectSelectedCandidateCount = createSelector(
  selectMessageDialogState,
  (state) => state.candidateIds.length,
);
export const selectGroupMemberCount = createSelector(
  selectMessageDialogState,
  (state) => state.groupMemberCount ?? 0,
);

const selectByTypeSelector = (_: RootState, type: "email" | "text") => type;
export const selectSelectedCandidatesByType = createSelector(
  selectSelectedCandidates,
  selectByTypeSelector,
  (candidates, type) =>
    type === "email" ? candidates.filter((c) => !!c.emailAddress) : candidates.filter((c) => !!c.phoneNumber),
);
export const selectSelectedCandidatesCountByType = createSelector(
  selectSelectedCandidates,
  selectByTypeSelector,
  (candidates, type) =>
    type === "email"
      ? candidates.filter((c) => !!c.emailAddress).length
      : candidates.filter((c) => !!c.phoneNumber).length,
);

export default MessageDialogSlice.reducer;
